import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LifeBuoy, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  open: "bg-amber-100 text-amber-800",
  in_progress: "bg-blue-100 text-blue-800",
  resolved: "bg-green-100 text-green-800",
};

export default function Support() {
  const [issues, setIssues] = useState([]);
  const [form, setForm] = useState({ subject: "", message: "", category: "General" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/issues/mine").then((r) => setIssues(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/issues", form);
      toast.success("Ticket submitted. Our team will respond soon.");
      setForm({ subject: "", message: "", category: "General" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-center gap-2 text-primary"><LifeBuoy className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.2em] font-bold">Support</span></div>
      <h1 className="font-display text-4xl font-semibold tracking-tighter mt-2">Need a hand?</h1>
      <p className="text-muted-foreground mt-2">Raise a ticket and our admins will get back to you.</p>

      <form onSubmit={submit} className="mt-8 bg-white border border-border rounded-sm p-6 space-y-4">
        <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} required className="mt-1 rounded-sm" data-testid="issue-subject" /></div>
        <div><Label>Message</Label><Textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} required rows={4} className="mt-1 rounded-sm" data-testid="issue-message" /></div>
        <Button type="submit" disabled={loading} className="rounded-sm" data-testid="issue-submit">{loading ? "Sending…" : "Submit ticket"}</Button>
      </form>

      <h2 className="font-display text-2xl font-semibold mt-12">Your tickets</h2>
      {issues.length === 0 ? (
        <p className="text-muted-foreground mt-4" data-testid="issues-empty">No tickets yet.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {issues.map((i) => (
            <div key={i.id} className="bg-white border border-border rounded-sm p-5" data-testid={`issue-${i.id}`}>
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-medium">{i.subject}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${STATUS[i.status]}`}>{i.status.replace("_", " ")}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{i.message}</p>
              {i.admin_response && (
                <div className="mt-3 border-l-2 border-primary pl-3 text-sm">
                  <div className="flex items-center gap-1 text-primary font-medium"><MessageSquare className="w-3.5 h-3.5" />Admin reply</div>
                  <p className="text-foreground/80 mt-1">{i.admin_response}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
